import { FunctionalUnitSelect } from '@components/FunctionalUnitSelect'
import { RppsAutocompleteByNameInput } from '@components/RppsAutocompleteByNameInput'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers-pro'
import { Member, RppsDto, UpdateMedicalOrderDto } from '@services/api'
import {
  HospitalUnitType,
  OrderDto,
  UpdateOrderDto,
  VisitRequestingOrganisationDto,
} from '@services/dtos'
import { FunctionalUnitDTO } from '@services/functionalUnitService'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { doGetOrder, doUpdateOrder } from '@state/reducers/orderReducer'
import { doUpdateVisit } from '@state/reducers/visitReducer'
import { doFindMembers } from '@state/thunks/memberThunk'
import { findAll } from '@state/thunks/siteThunk'
import RequestingOrganisationSelector from '../../containers/app/RequestingOrganisationSelector'
import { capitalize, upperCase } from 'lodash'
import {
  Bed,
  Building2,
  CalendarDays,
  ClipboardPlus,
  Clock3,
  FileText,
  Hospital,
  Info,
  PenLine,
  Save,
  Stethoscope,
  UserRound,
  X,
} from 'lucide-react'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { MedicalOrderSchema } from '../../utils/schemas'

type Props = {
  order: OrderDto
  editByDefault?: boolean
  onClose?: () => void
}

const DetailItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}) => (
  <div className="flex min-w-0 gap-3">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-purple-50 text-purple-700">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-normal text-gray-500">
        {label}
      </div>
      <div className="mt-1 truncate text-sm font-medium text-gray-950">
        {value || '-'}
      </div>
    </div>
  </div>
)

const IconFrame = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-purple-50 text-purple-700 shadow-sm">
    {children}
  </div>
)

const RequiredLabel = ({
  children,
  required = false,
}: {
  children: React.ReactNode
  required?: boolean
}) => (
  <span className="mb-1 block text-xs font-medium text-gray-600">
    {children}
    {required && <span className="ml-1 text-red-500">*</span>}
  </span>
)

const SectionTitle = ({
  icon,
  title,
}: {
  icon: React.ReactNode
  title: string
}) => (
  <div className="mb-5 flex items-center gap-3 text-purple-700">
    <IconFrame>{icon}</IconFrame>
    <h3 className="text-base font-bold">{title}</h3>
  </div>
)

const FieldShell = ({
  icon,
  label,
  required,
  children,
}: {
  icon: React.ReactNode
  label: string
  required?: boolean
  children: React.ReactNode
}) => (
  <div className="flex min-w-0 gap-3">
    <IconFrame>{icon}</IconFrame>
    <div className="min-w-0 flex-1">
      <RequiredLabel required={required}>{label}</RequiredLabel>
      {children}
    </div>
  </div>
)

const DoctorIdentity = ({
  doctor,
  onClear,
}: {
  doctor: RppsDto
  onClear: () => void
}) => {
  const initials = `${doctor.firstName?.charAt(0) ?? ''}${
    doctor.lastName?.charAt(0) ?? ''
  }`.toUpperCase()

  return (
    <div className="flex min-h-[52px] items-center justify-between gap-3 rounded-md border border-purple-200 bg-purple-50 px-3 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-700">
          {initials || 'DR'}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-gray-950">
            {capitalize(doctor.firstName)} {doctor.lastName}
          </div>
          {!!doctor.rppsNumber && (
            <div className="mt-0.5 inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-semibold text-purple-700">
              RPPS: {doctor.rppsNumber}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-purple-700 hover:bg-purple-100"
        aria-label="Retirer le médecin"
      >
        <X size={18} />
      </button>
    </div>
  )
}

const DoctorPanel = ({
  title,
  value,
  onChange,
}: {
  title: string
  value: RppsDto | null
  onChange: (value: RppsDto | null) => void
}) => (
  <div className="rounded-lg border border-gray-200 p-5">
    <SectionTitle icon={<Stethoscope size={19} />} title={title} />
    {value ? (
      <>
        <DoctorIdentity doctor={value} onClear={() => onChange(null)} />
        <FormControlLabel
          sx={{ mt: 2 }}
          control={<Checkbox size="small" />}
          label="Chercher dans la base locale"
        />
      </>
    ) : (
      <RppsAutocompleteByNameInput
        onChange={onChange}
        value={value}
        label=""
        size="small"
      />
    )}
  </div>
)

export const MedicalOrderForm: React.FC<Props> = ({
  order,
  editByDefault,
  onClose,
}) => {
  const dispatch = useAppDispatch()

  // ------------------------- state ------------------------------
  const { sites, members, currentUser } = useAppSelector(
    ({ site, member, auth }) => ({
      sites: site.sites,
      members: member.members,
      currentUser: auth.user as Member,
    }),
  )

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateMedicalOrderDto>({
    resolver: yupResolver(MedicalOrderSchema),
    defaultValues: {
      siteId: order.site?.id,
      attendingDoctor: order.attendingDoctor,
      referringDoctor: order.referringDoctor,
      consultingDoctor: order.consultingDoctor,
      secretaryId: order.secretary?.id || currentUser?.id,
      registerDate: order.registeredAt
        ? (moment(order.registeredAt).format('DD-MM-YYYY') as any)
        : undefined,
      registerTime: order.registeredAt
        ? (moment(order.registeredAt).format('HH:mm') as any)
        : undefined,
      isEmergency: order.isEmergency,
      isHoliday: order.isHoliday,
      isNightShift: order.isNightShift,
      astreinte: order.astreinte,
      accomodationFunctionalUnitId: order.accommodationFunctionalUnit?.id,
      medicalFunctionalUnitId: order.medicalFunctionalUnit?.id,
    },
  })

  const attendingDoctor = watch('attendingDoctor')
  const referringDoctor = watch('referringDoctor')
  const consultingDoctor = watch('consultingDoctor')
  const registerDate = watch('registerDate')
  const registerTime = watch('registerTime')
  const isEmergency = watch('isEmergency')
  const isNightShift = watch('isNightShift')
  const isHoliday = watch('isHoliday')
  const astreinte = watch('astreinte')
  const secretaryId = watch('secretaryId')
  const siteId = watch('siteId')

  const formatMemberName = (
    member?: { firstName?: string; lastName?: string } | null,
  ) =>
    member
      ? `${capitalize(member.firstName)} ${upperCase(member.lastName)}`
      : '-'

  const plannedDate = order.medicalOrders?.find(
    (medicalOrder) => medicalOrder.plannedDate,
  )?.plannedDate

  const compactInputSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: 44,
      borderRadius: '6px',
      backgroundColor: '#fff',
    },
  } as const

  const [isEditing, setisEditing] = useState(editByDefault)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [familyDoctor, setFamilyDoctor] = useState<RppsDto | null>(
    order.patient.familyDoctor
      ? ({
          rppsNumber: order.patient.familyDoctor.rppsNumber,
          firstName: order.patient.familyDoctor.firstName,
          lastName: order.patient.familyDoctor.lastName,
          skillLabel: order.patient.familyDoctor.skill,
          profession: order.patient.familyDoctor.profession,
          category: order.patient.familyDoctor.category,
        } as RppsDto)
      : null,
  )

  const [accommodationFunctionalUnit, setAccommodationFunctionalUnit] =
    useState<FunctionalUnitDTO | null>(
      order.accommodationFunctionalUnit || null,
    )
  const [medicalFunctionalUnit, setMedicalFunctionalUnit] =
    useState<FunctionalUnitDTO | null>(order.medicalFunctionalUnit || null)
  const [requestingOrganisation, setRequestingOrganisation] =
    useState<VisitRequestingOrganisationDto | null>(
      order.visit?.requestingOrganisation ?? null,
    )

  // ------------------------- useEffects -------------------------
  useEffect(() => {
    setRequestingOrganisation(order.visit?.requestingOrganisation ?? null)
  }, [order.visit?.requestingOrganisation])

  useEffect(() => {
    dispatch(findAll())
  }, [dispatch])

  useEffect(() => {
    dispatch(doFindMembers({ limit: 400 }))
  }, [dispatch])

  useEffect(() => {
    if (currentUser && !secretaryId) {
      setValue('secretaryId', currentUser.id)
    }
  }, [currentUser, secretaryId, setValue])

  // ------------------------- Functions ----------------
  const onSubmit = (data: UpdateMedicalOrderDto) => {
    setIsSubmitting(true)
    const dto: UpdateOrderDto = {
      ...data,
      registerDate: registerDate || null,
      registerTime: registerTime || null,
    }

    if (
      familyDoctor &&
      order.patient?.familyDoctor?.rppsNumber !== familyDoctor?.rppsNumber
    ) {
      dto.familyDoctor = familyDoctor
    } else if (!familyDoctor && order.patient?.familyDoctor?.id) {
      dto.familyDoctor = null
    }

    const visitUpdateNeeded =
      !!order.visit?.id &&
      requestingOrganisation?.id !== order.visit?.requestingOrganisation?.id

    dispatch(doUpdateOrder({ id: order.id, dto }))
      .unwrap()
      .then(async () => {
        if (visitUpdateNeeded && order.visit?.id) {
          await dispatch(
            doUpdateVisit({
              id: order.visit.id,
              dto: {
                requestingOrganisationId: requestingOrganisation?.id ?? null,
              },
            }),
          ).unwrap()
          await dispatch(doGetOrder(order.id)).unwrap()
        }
        setisEditing(false)
        dispatch(
          enqueueSnackbar({
            message: 'Mise à jour effectuée',
            options: { variant: 'success' },
          }),
        )
      })
      .catch(() => {
        dispatch(
          enqueueSnackbar({
            message: 'Erreur lors de la mise à jour',
            options: { variant: 'error' },
          }),
        )
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleAttendingDoctorChange = (id: number) => {
    const member = members.datas.find((m) => m.id === id)
    if (member) {
      setValue('attendingDoctor', member)
      setValue('attendingDoctorId', id)
    }
  }

  const handleConsultingDoctorChange = (id: number) => {
    const member = members.datas.find((m) => m.id === id)
    if (member) {
      setValue('consultingDoctor', member)
      setValue('consultingDoctorId', id)
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-purple-100 bg-purple-50 text-purple-700">
            <FileText size={32} strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-bold leading-tight text-gray-950">
                Demande
              </h2>
              <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-purple-700">
                En cours
              </span>
            </div>
            <div className="mt-4">
              <div className="text-xs font-semibold text-gray-500">
                Numéro de séjour
              </div>
              <div className="mt-1 text-lg font-bold text-gray-950">
                {order.visit?.identifier || ' - '}
              </div>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {isEditing ? (
            <>
              <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Save size={16} />
                  )
                }
                disabled={isSubmitting}
                sx={{
                  height: 42,
                  borderRadius: '6px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              >
                Enregistrer
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setisEditing(false)
                }}
                disabled={isSubmitting}
                startIcon={<X size={16} />}
                sx={{
                  height: 42,
                  borderRadius: '6px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              >
                Annuler
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setisEditing(true)
                }}
                variant="contained"
                color="secondary"
                startIcon={<PenLine size={16} />}
                sx={{
                  height: 42,
                  borderRadius: '6px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  boxShadow: '0 8px 18px rgba(91, 33, 182, 0.22)',
                }}
              >
                Modifier
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  height: 42,
                  borderRadius: '6px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              >
                Fermer
              </Button>
            </>
          )}
        </div>
      </div>
      {isEditing ? (
        <>
          {isSubmitting && <LinearProgress className="mb-4 w-full" />}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <section className="rounded-lg border border-gray-200 p-5">
              <SectionTitle
                icon={<ClipboardPlus size={19} />}
                title="Informations générales"
              />
              <div className="grid gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-4">
                <FieldShell icon={<Hospital size={18} />} label="Site" required>
                  <FormControl fullWidth size="small">
                    <InputLabel id="site-label">Site</InputLabel>
                    <Select
                      labelId="site-label"
                      label="Site"
                      value={siteId ?? ''}
                      error={!!errors.siteId}
                      onChange={(event) =>
                        setValue('siteId', Number(event.target.value))
                      }
                      sx={compactInputSx}
                    >
                      {sites.datas.map((site) => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldShell>
                <FieldShell
                  icon={<Bed size={18} />}
                  label="UF Hébergement"
                  required
                >
                  <FunctionalUnitSelect
                    value={accommodationFunctionalUnit}
                    onChange={(unit) => {
                      setAccommodationFunctionalUnit(unit)
                      setValue('accomodationFunctionalUnitId', unit?.id || null)
                    }}
                    functionType={HospitalUnitType.ACCOMMODATION}
                    label="UF Hébergement"
                    size="small"
                  />
                </FieldShell>
                <FieldShell
                  icon={<ClipboardPlus size={18} />}
                  label="UF Médicale"
                  required
                >
                  <FunctionalUnitSelect
                    value={medicalFunctionalUnit}
                    onChange={(unit) => {
                      setMedicalFunctionalUnit(unit)
                      setValue('medicalFunctionalUnitId', unit?.id || null)
                    }}
                    functionType={HospitalUnitType.MEDICAL}
                    label="UF Médicale"
                    size="small"
                  />
                </FieldShell>
                <FieldShell
                  icon={<Building2 size={18} />}
                  label="Organisation prescriptrice"
                  required
                >
                  <RequestingOrganisationSelector
                    value={requestingOrganisation}
                    onChange={setRequestingOrganisation}
                    siteId={siteId ?? order.siteId}
                    size="small"
                  />
                </FieldShell>
                <FieldShell
                  icon={<UserRound size={18} />}
                  label="Practicien"
                  required
                >
                  <Autocomplete
                    options={members.datas.map((member) => ({
                      label: `${member.firstName} ${member.lastName}`,
                      value: member.id,
                    }))}
                    getOptionLabel={(option) => option.label}
                    onChange={(_, v) => {
                      if (v) {
                        handleAttendingDoctorChange(v.value)
                      } else {
                        setValue('attendingDoctor', null)
                      }
                    }}
                    sx={compactInputSx}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                    value={
                      attendingDoctor
                        ? {
                            value: attendingDoctor.id,
                            label: `${attendingDoctor.firstName} ${attendingDoctor.lastName}`,
                          }
                        : null
                    }
                  />
                </FieldShell>
                <FieldShell
                  icon={<UserRound size={18} />}
                  label="Médecin consultant"
                >
                  <Autocomplete
                    options={members.datas.map((member) => ({
                      label: `${member.firstName} ${member.lastName}`,
                      value: member.id,
                    }))}
                    getOptionLabel={(option) => option.label}
                    onChange={(_, v) => {
                      if (v) {
                        handleConsultingDoctorChange(v.value)
                      } else {
                        setValue('consultingDoctor', null)
                      }
                    }}
                    sx={compactInputSx}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                    value={
                      consultingDoctor
                        ? {
                            value: consultingDoctor.id,
                            label: `${consultingDoctor.firstName} ${consultingDoctor.lastName}`,
                          }
                        : null
                    }
                  />
                </FieldShell>
                <FieldShell
                  icon={<PenLine size={18} />}
                  label="Secrétaire"
                  required
                >
                  <Autocomplete
                    options={members.datas.map((member) => ({
                      label: `${member.firstName} ${member.lastName}`,
                      value: member.id,
                    }))}
                    getOptionLabel={(option) => option.label}
                    onChange={(_, v) => {
                      if (v) {
                        setValue('secretaryId', v.value)
                      } else {
                        setValue('secretaryId', undefined)
                      }
                    }}
                    sx={compactInputSx}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                    value={
                      secretaryId
                        ? (() => {
                            const secretary = members.datas.find(
                              (m) => m.id === secretaryId,
                            )
                            return secretary
                              ? {
                                  value: secretary.id,
                                  label: `${secretary.firstName} ${secretary.lastName}`,
                                }
                              : null
                          })()
                        : null
                    }
                  />
                </FieldShell>
              </div>
              <FormControlLabel
                sx={{ mt: 3 }}
                control={<Checkbox size="small" />}
                label="Chercher dans la base locale"
              />
            </section>

            <div className="grid gap-4 xl:grid-cols-[1fr_200px_1fr]">
              {!order.patient.isForeigner && (
                <DoctorPanel
                  title="Médecin Prescripteur"
                  value={referringDoctor || null}
                  onChange={(v) => setValue('referringDoctor', v)}
                />
              )}
              <div className="flex min-h-[160px] items-center justify-center rounded-lg px-4 text-center">
                {order.patient.familyDoctor && (
                  <FormControlLabel
                    label="Médecin prescripteur est le médecin traitant"
                    labelPlacement="top"
                    sx={{
                      m: 0,
                      gap: 1.5,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        lineHeight: 1.35,
                      },
                    }}
                    control={
                      <Switch
                        onChange={(_, checked) => {
                          if (checked) {
                            const doctor = {
                              rppsNumber: order.patient.familyDoctor.rppsNumber,
                              firstName: order.patient.familyDoctor.firstName,
                              lastName: order.patient.familyDoctor.lastName,
                            } as RppsDto
                            setValue('referringDoctor', doctor)
                            setFamilyDoctor(doctor)
                          } else {
                            setValue('referringDoctor', null)
                          }
                        }}
                        defaultChecked={
                          order.patient.familyDoctor?.rppsNumber ===
                          referringDoctor?.rppsNumber
                        }
                        size="small"
                      />
                    }
                  />
                )}
              </div>
              <DoctorPanel
                title="Médecin Traitant"
                value={familyDoctor || null}
                onChange={(v) => setFamilyDoctor(v)}
              />
            </div>

            <section className="rounded-lg border border-gray-200 p-5">
              <SectionTitle icon={<CalendarDays size={19} />} title="Accueil" />
              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[220px]">
                  <RequiredLabel required>Date d&apos;accueil</RequiredLabel>
                  <DatePicker
                    value={moment(registerDate, 'DD-MM-YYYY') as any}
                    onChange={(v) => {
                      setValue('registerDate', v?.format('DD-MM-YYYY'))
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: compactInputSx,
                      },
                    }}
                  />
                </div>
                <div className="pb-3 text-sm font-medium text-gray-700">à</div>
                <div className="w-[150px]">
                  <RequiredLabel required>à</RequiredLabel>
                  <TimePicker
                    value={moment(registerTime, 'HH:mm') as any}
                    onChange={(v) =>
                      setValue('registerTime', v?.format('HH:mm'))
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: compactInputSx,
                      },
                    }}
                  />
                </div>

                <Button
                  onClick={() => {
                    setValue('registerDate', moment().format('DD-MM-YYYY'))
                    setValue('registerTime', moment().format('HH:mm'))
                  }}
                  variant="contained"
                  size="small"
                  startIcon={<CalendarDays size={16} />}
                  sx={{
                    height: 42,
                    minWidth: 138,
                    borderRadius: '6px',
                    bgcolor: '#333',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    '&:hover': { bgcolor: '#111' },
                  }}
                >
                  Maintenant
                </Button>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-1">
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={(ev) =>
                          setValue('isEmergency', ev.target.checked)
                        }
                        defaultChecked={isEmergency}
                        size="small"
                      />
                    }
                    label="Urgent"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={(ev) =>
                          setValue('astreinte', ev.target.checked)
                        }
                        defaultChecked={astreinte}
                        size="small"
                      />
                    }
                    label="Astreinte"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={(ev) =>
                          setValue('isNightShift', ev.target.checked)
                        }
                        defaultChecked={isNightShift}
                        size="small"
                      />
                    }
                    label="Nuit"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={(ev) =>
                          setValue('isHoliday', ev.target.checked)
                        }
                        defaultChecked={isHoliday}
                        size="small"
                      />
                    }
                    label="Dimanche / J. férié"
                  />
                </div>
              </div>
            </section>

            <div className="flex items-center gap-3 rounded-lg bg-purple-50 px-5 py-4 text-purple-800">
              <Info size={18} />
              <span>
                Les champs marqués d’un <span className="font-bold">*</span>{' '}
                sont obligatoires.
              </span>
            </div>
          </form>
        </>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 border-l border-gray-200 pl-0 lg:grid-cols-4 lg:pl-6">
            <DetailItem
              icon={<CalendarDays size={19} />}
              label="Date prévue"
              value={
                plannedDate ? (
                  <span>
                    {moment(plannedDate).format('DD/MM/YYYY')}
                    <br />
                    <span className="font-normal">
                      {moment(plannedDate).format('HH:mm')}
                    </span>
                  </span>
                ) : (
                  '-'
                )
              }
            />
            <DetailItem
              icon={<Hospital size={19} />}
              label="Site"
              value={order.site?.label || '-'}
            />
            <DetailItem
              icon={<UserRound size={19} />}
              label="Practicien"
              value={formatMemberName(attendingDoctor)}
            />
            <DetailItem
              icon={<Building2 size={19} />}
              label="Organisation"
              value={requestingOrganisation?.name || '-'}
            />
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="grid gap-x-6 gap-y-7 sm:grid-cols-2 xl:grid-cols-4">
              <DetailItem
                icon={<UserRound size={19} />}
                label="Médecin consultant"
                value={formatMemberName(consultingDoctor)}
              />
              <DetailItem
                icon={<Bed size={19} />}
                label="UF hébergement"
                value={order.accommodationFunctionalUnit?.name || '-'}
              />
              <DetailItem
                icon={<ClipboardPlus size={19} />}
                label="UF médicale"
                value={order.medicalFunctionalUnit?.name || '-'}
              />
              <DetailItem
                icon={<UserRound size={19} />}
                label="Médecin prescripteur"
                value={
                  referringDoctor ? (
                    <>
                      <span className="capitalize">
                        {referringDoctor.firstName}
                      </span>{' '}
                      <span className="uppercase">
                        {referringDoctor.lastName}
                      </span>{' '}
                      {!!referringDoctor.skillLabel &&
                        referringDoctor.skillLabel}
                    </>
                  ) : (
                    '-'
                  )
                }
              />
              <DetailItem
                icon={<PenLine size={19} />}
                label="Secrétaire"
                value={formatMemberName(order.secretary)}
              />
              <DetailItem
                icon={<Stethoscope size={19} />}
                label="Médecin traitant"
                value={formatMemberName(order.patient.familyDoctor)}
              />
              <DetailItem
                icon={<Clock3 size={19} />}
                label="Accueil date et heure"
                value={
                  order.registeredAt
                    ? moment(order.registeredAt).format('DD/MM/YYYY [à] HH:mm')
                    : '-'
                }
              />
              <DetailItem
                icon={<Building2 size={19} />}
                label="Organisation prescriptrice"
                value={order.visit?.requestingOrganisation?.name || '-'}
              />
            </div>
          </div>

          {(order.isEmergency || order.isNightShift || order.isHoliday) && (
            <div className="flex flex-wrap gap-2">
              {order.isEmergency && (
                <Chip
                  label="Urgent"
                  style={{
                    backgroundColor: '#c44569',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                  size="small"
                />
              )}
              {order.isNightShift && (
                <Chip
                  label="Nuit"
                  style={{
                    backgroundColor: '#30336b',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                  size="small"
                />
              )}
              {order.isHoliday && (
                <Chip
                  label="Dimanche / J. férié"
                  style={{
                    backgroundColor: '#f0932b',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                  size="small"
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MedicalOrderForm
